import type { FrIconClassName } from '@codegouvfr/react-dsfr';
import Card from '@codegouvfr/react-dsfr/Card';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import type { RegisteredLinkProps } from '@codegouvfr/react-dsfr/link';
import Tag from '@codegouvfr/react-dsfr/Tag';
import clsx from 'clsx';
import { sortBy, sumBy, uniqBy } from 'lodash-es';
import {
  isCampaignLaunched,
  type ProgrammingPlanChecked
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import type { CSSProperties } from 'react';
import { pluralize } from 'src/utils/stringUtils';
import { assert, type Equals } from 'tsafe';

import { ProgrammingPlanSettingsBadge } from '../ProgrammingPlanSettingsBadge/ProgrammingPlanSettingsBadge';
import './ProgrammingPlanSettingsCard.scss';

type Props = {
  title: string;
  programmingPlans: ProgrammingPlanChecked[];
  linkProps: RegisteredLinkProps;
  withPlanCount?: boolean;
};

export const ProgrammingPlanSettingsCard = ({
  title,
  programmingPlans,
  linkProps,
  withPlanCount,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const subPlanCount = sumBy(programmingPlans, (plan) => plan.subPlans.length);

  const nationalCoordinators = sortBy(
    uniqBy(
      programmingPlans.flatMap((plan) => plan.nationalCoordinators),
      'id'
    ),
    ({ name, email }) => name ?? email
  ).map(({ name, email }) => name ?? email);

  const launchedPlanCount = programmingPlans.filter(isCampaignLaunched).length;

  const campaign: {
    label: string;
    icon: FrIconClassName;
    style?: CSSProperties;
  } =
    launchedPlanCount === 0
      ? { label: 'Campagne non lancée', icon: 'fr-icon-time-line' }
      : launchedPlanCount === programmingPlans.length
        ? {
            label: 'Campagne lancée',
            icon: 'fr-icon-success-fill',
            style: {
              color: 'var(--text-default-info)',
              backgroundColor: 'var(--background-contrast-info)'
            }
          }
        : { label: 'Campagne en partie lancée', icon: 'fr-icon-time-line' };

  return (
    <Card
      className="programming-plan-settings-card"
      title={title}
      titleAs="h6"
      enlargeLink={true}
      linkProps={linkProps}
      start={
        <ProgrammingPlanSettingsBadge
          small
          programmingPlans={programmingPlans}
        />
      }
      desc={
        <>
          <Tag
            as="span"
            small
            iconId={campaign.icon}
            className={clsx('no-wrap', cx('fr-mb-1w'))}
            style={campaign.style}
          >
            {campaign.label}
          </Tag>
          <span className={cx('fr-text--xs', 'fr-mb-0')}>
            <span className="icon-text ">
              <span
                className={cx('fr-icon-folder-2-line', 'fr-icon--sm')}
              ></span>
              {withPlanCount && (
                <>
                  {pluralize(programmingPlans.length, { preserveCount: true })(
                    'plan'
                  )}
                  {' / '}
                </>
              )}
              {pluralize(subPlanCount, { preserveCount: true })('sous-plan')}
            </span>
          </span>
          {nationalCoordinators.length > 0 && (
            <span className={cx('fr-text--xs', 'fr-mb-0')}>
              <span className="icon-text">
                <span className={cx('fr-icon-user-line', 'fr-icon--sm')}></span>
                {nationalCoordinators.join(', ')}
              </span>
            </span>
          )}
        </>
      }
    />
  );
};
